import axios from "axios";
import Swal from "sweetalert2";
import CryptoJS from "crypto-js";
import {
  showSubscriptionExpiredPopup,
  showUpgradeRequiredPopup,
} from "@/utils/subscriptionAccess";

const ZOHO_RESPONSE_KEY = import.meta.env.VITE_ZOHO_RESPONSE_KEY || "";
const ZOHO_RESPONSE_IV = import.meta.env.VITE_ZOHO_RESPONSE_IV || "";

const getCookie = (name) => {
  if (typeof document === "undefined") return null;
  const cookies = document.cookie ? document.cookie.split(";") : [];
  const key = `${name}=`;
  for (const raw of cookies) {
    const cookie = raw.trim();
    if (cookie.startsWith(key)) {
      return decodeURIComponent(cookie.slice(key.length));
    }
  }
  return null;
};

function decodeZohoPayload(wrapped) {
  try {
    if (!ZOHO_RESPONSE_KEY || !ZOHO_RESPONSE_IV) return null;

    const { b, s } = wrapped || {};
    if (typeof b !== "string" || typeof s !== "string") return null;

    // Verify HMAC signature
    const expectedSig = CryptoJS.HmacSHA256(b, ZOHO_RESPONSE_KEY)
      .toString(CryptoJS.enc.Hex);
    if (expectedSig !== s) {
      console.warn("Zoho payload signature mismatch");
      return null;
    }

    // Derive AES key (32 bytes) and IV (16 bytes) exactly like backend
    const ZOHO_KEY = CryptoJS.SHA256(ZOHO_RESPONSE_KEY); // 32 bytes
    const ivHash = CryptoJS.SHA256(ZOHO_RESPONSE_IV);
    const ZOHO_IV = CryptoJS.lib.WordArray.create(ivHash.words.slice(0, 4), 16); // 16 bytes

    // Decrypt AES-256-CBC, ciphertext is base64 string `b`
    const decrypted = CryptoJS.AES.decrypt(b, ZOHO_KEY, {
      iv: ZOHO_IV,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });

    const jsonStr = decrypted.toString(CryptoJS.enc.Utf8);
    if (!jsonStr) return null;

    return JSON.parse(jsonStr);
  } catch (e) {
    console.warn("Failed to decode Zoho payload", e);
    return null;
  }
}

const CACHE_TTL = 30 * 1000; // 30 seconds of reuse for GET requests
const MAX_CACHE_ENTRIES = 50;
const responseCache = new Map();
let activeRequestCount = 0;

const emitRequestState = (phase) => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent("tarh:request-state", {
      detail: {
        phase,
        activeCount: activeRequestCount,
      },
    })
  );
};

const normalizeForCache = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => normalizeForCache(item));
  }
  if (value && typeof value === "object") {
    return Object.keys(value)
      .sort()
      .reduce((acc, key) => {
        acc[key] = normalizeForCache(value[key]);
        return acc;
      }, {});
  }
  return value;
};

const buildCacheKey = (config) => {
  const method = (config.method || "get").toLowerCase();
  const base = config.baseURL ? config.baseURL.replace(/\/+$/, "") : "";
  const urlPath = (config.url || "").replace(/^\/+/, "");
  const langHeader =
    config?.headers?.["X-Lang"] || config?.headers?.["x-lang"] || "";
  const params =
    config.params && Object.keys(config.params).length > 0
      ? JSON.stringify(normalizeForCache(config.params))
      : "";
  return [method, `${base}/${urlPath}`, params, langHeader].join("::");
};

const shouldBypassCache = (config) => {
  if (!config) return true;
  const method = (config.method || "get").toLowerCase();
  if (method !== "get") return true;
  if (config.forceRefresh) return true;
  const cacheControl =
    config.headers?.["Cache-Control"] || config.headers?.["cache-control"];
  if (cacheControl && cacheControl.includes("no-cache")) return true;
  if (config.params && config.params.nocache) return true;
  return false;
};

const enforceCacheLimit = () => {
  while (responseCache.size > MAX_CACHE_ENTRIES) {
    const oldestKey = responseCache.keys().next().value;
    responseCache.delete(oldestKey);
  }
};

const cacheResponse = (key, response) => {
  if (!key) return;
  responseCache.set(key, {
    timestamp: Date.now(),
    payload: {
      data: response.data,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
    },
  });
  enforceCacheLimit();
};

const api = axios.create({
  // Toggle between direct backend and an obfuscated same-origin reverse-proxy path.
  // - Set `VITE_USE_EDGE_PROXY=true` to use `VITE_EDGE_ROUTE` (default: "/_r9KpQ1x7N4")
  // - Otherwise uses `VITE_API_BASE_URL` (default: "http://localhost:8000/api")
  baseURL:
    String(import.meta.env.VITE_USE_EDGE_PROXY || "").toLowerCase() === "true" ||
    String(import.meta.env.VITE_USE_EDGE_PROXY || "") === "1"
      ? import.meta.env.VITE_EDGE_ROUTE || "/_r9KpQ1x7N4"
      : import.meta.env.VITE_API_BASE_URL || "http://localhost:8000/api",
  withCredentials: true,
  timeout: 60000,
  headers: {
    "X-Lang": localStorage.getItem("lang") || "ar", // Send language preference
  },
});

// Always-on CSRF auto-retry (works even if setupInterceptors() wasn't called yet).
api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const status = err?.response?.status;
    const data = err?.response?.data || {};
    const csrfError =
      status === 422 &&
      (data?.error === "CSRF token mismatch." ||
        data?.message === "CSRF token mismatch." ||
        data?.error === "CSRF origin check failed." ||
        data?.message === "CSRF origin check failed.");

    const isCsrfCookieRequest =
      typeof err?.config?.url === "string" && err.config.url.includes("/csrf-cookie");

    if (csrfError && err?.config && !err.config.__csrfRetried && !isCsrfCookieRequest) {
      try {
        err.config.__csrfRetried = true;
        await api.get("/csrf-cookie", { forceRefresh: true });

        const xsrfToken = getCookie("XSRF-TOKEN");
        if (xsrfToken) {
          err.config.headers = err.config.headers || {};
          err.config.headers["X-XSRF-TOKEN"] = xsrfToken;
        }

        return api.request(err.config);
      } catch (_) {
        // fall through
      }
    }

    return Promise.reject(err);
  }
);

api.interceptors.request.use((config) => {
  const method = (config.method || "get").toLowerCase();
  config.metadata = config.metadata || {};
  const currentLang = localStorage.getItem("lang") || "ar";
  config.headers = config.headers || {};
  config.headers["X-Lang"] = currentLang;

  if (method !== "get" && method !== "head" && method !== "options") {
    const xsrfToken = getCookie("XSRF-TOKEN");
    if (xsrfToken) {
      config.headers["X-XSRF-TOKEN"] = xsrfToken;
    }
  }

  if (!shouldBypassCache(config)) {
    const key = buildCacheKey(config);
    config.metadata.cacheKey = key;
    const cached = responseCache.get(key);
    if (cached) {
      const isExpired = Date.now() - cached.timestamp > CACHE_TTL;
      if (!isExpired) {
        config.metadata.cacheHit = true;
        config.metadata.trackRequest = false;
        return {
          ...config,
          adapter: () =>
            Promise.resolve({
              data: cached.payload.data,
              status: cached.payload.status,
              statusText: cached.payload.statusText,
              headers: cached.payload.headers,
              config,
              request: {},
            }),
        };
      }
      responseCache.delete(key);
    }
  } else if (method === "get") {
    config.metadata.skipCacheStore = true;
  } else {
    config.metadata.clearCache = true;
  }

  if (config.metadata.trackRequest === undefined) {
    config.metadata.trackRequest = true;
  }

  if (config.metadata.trackRequest) {
    activeRequestCount += 1;
    emitRequestState("start");
  }

  return config;
});

// Setup function to inject dispatch
export const setupInterceptors = (dispatch) => {
  let lastNetworkAlertAt = 0;
  const NETWORK_ALERT_COOLDOWN_MS = 15000;
  const finalizeTrackedRequest = (config) => {
    if (!config?.metadata?.trackRequest) return;
    activeRequestCount = Math.max(0, activeRequestCount - 1);
    emitRequestState("end");
  };

  api.interceptors.response.use(
    (res) => {
      // Transparent decode for encoded Zoho responses
      const data = res && res.data;
      if (
        data &&
        typeof data === "object" &&
        data._tz === 1 &&
        typeof data.b === "string"
      ) {
        const decoded = decodeZohoPayload(data);
        if (decoded !== null) {
          // Replace with original Zoho structure so the app sees old shape
          res.data = decoded;
        }
      }

      const cacheKey = res?.config?.metadata?.cacheKey;
      const cacheHit = res?.config?.metadata?.cacheHit;
      const skipStore = res?.config?.metadata?.skipCacheStore;
      const method = (res?.config?.method || "get").toLowerCase();

      if (!cacheHit && !skipStore && method === "get" && cacheKey) {
        cacheResponse(cacheKey, res);
      }

      if (res?.config?.metadata?.clearCache) {
        responseCache.clear();
      }

      finalizeTrackedRequest(res?.config);

      return res;
    },
    async (err) => {
      const originalConfig = err?.config;
      const status = err?.response?.status;
      const isCanceled =
        err?.code === "ERR_CANCELED" || err?.message === "canceled";
      const isNetworkError =
        !isCanceled &&
        (err?.code === "ERR_NETWORK" ||
          err?.message === "Network Error" ||
          (!err?.response && err?.request));

      // If the JWT cookie exists but the XSRF cookie is missing/stale, the API returns 422.
      // Refresh the CSRF cookie and retry the original request once.
      const csrfError =
        status === 422 &&
        (err?.response?.data?.error === "CSRF token mismatch." ||
          err?.response?.data?.error === "CSRF origin check failed.");

      if (csrfError && err?.config && !err.config.__csrfRetried) {
        try {
          err.config.__csrfRetried = true;
          finalizeTrackedRequest(originalConfig);
          if (err.config.metadata) {
            err.config.metadata.trackRequest = true;
          }
          await api.get("/csrf-cookie", { forceRefresh: true });

          const xsrfToken = getCookie("XSRF-TOKEN");
          if (xsrfToken) {
            err.config.headers = err.config.headers || {};
            err.config.headers["X-XSRF-TOKEN"] = xsrfToken;
          }

          return api.request(err.config);
        } catch (_) {
          // Fall through to standard error handling.
        }
      }
      const currentPath = window.location?.pathname || "";
      const authPages = ["/login", "/register", "/forgot", "/reset"];
      const requestUrl = err?.config?.url || "";
      const ignoredEndpoints = [
        "/login",
        "/register",
        "/forgot-password",
        "/reset-password",
        "/user",
        "/subscription/status",
      ];
      const shouldSkipRedirect =
        authPages.includes(currentPath) ||
        ignoredEndpoints.some((endpoint) => requestUrl.includes(endpoint));

      if (isCanceled) {
        return Promise.reject(err);
      }

      if (isNetworkError) {
        const now = Date.now();
        const shouldShowNetworkAlert =
          !shouldSkipRedirect && now - lastNetworkAlertAt > NETWORK_ALERT_COOLDOWN_MS;

        if (shouldShowNetworkAlert) {
          lastNetworkAlertAt = now;
          Swal.fire({
            text: "Network error or server unreachable. Please check your connection and try again.",
            icon: "warning",
            iconColor: "#dc2626",
          });
        }
        return Promise.reject(err);
      } else if (status === 401 && !shouldSkipRedirect) {
        dispatch({ type: "auth/logout" });
        window.location.href = "/login";
      } else if (status === 403) {
        const reason =
          err?.response?.data?.code ||
          err?.response?.data?.reason ||
          err?.response?.data?.message ||
          "";
        const isSubscriptionExpired =
          (typeof reason === "string" &&
            reason.toLowerCase().includes("subscription_expired")) ||
          err?.response?.data?.code === "subscription_expired";
        const isPlanUpgradeRequired =
          typeof reason === "string" &&
          reason.toLowerCase().includes("plan_upgrade_required");
        const isAccountInactive =
          err?.response?.data?.code === "account_inactive" ||
          (typeof reason === "string" &&
            reason.toLowerCase().includes("account_inactive")) ||
          (typeof reason === "string" &&
            reason.toLowerCase().includes("access to your account")) ||
          (typeof reason === "string" &&
            reason.toLowerCase().includes("don't have access"));
        const isSubscriptionError =
          typeof reason === "string" &&
          reason.toLowerCase().includes("subscription");

        if (isSubscriptionExpired && !shouldSkipRedirect) {
          showSubscriptionExpiredPopup();
          dispatch({ type: "subscriptions/forceUnsubscribed" });
        } else if (isPlanUpgradeRequired && !shouldSkipRedirect) {
          showUpgradeRequiredPopup();
        } else if (isAccountInactive && !authPages.includes(currentPath)) {
          Swal.fire({
            icon: "warning",
            iconColor: "#dc2626",
            text:
              err?.response?.data?.error ||
              err?.response?.data?.message ||
              "You don't have access to your account.",
            confirmButtonText: "OK",
          });
          dispatch({ type: "auth/logout" });
          window.location.href = "/login";
        } else if (isSubscriptionError) {
          dispatch({ type: "subscriptions/forceUnsubscribed" });
        }
      }

      if (err?.config?.metadata?.clearCache) {
        responseCache.clear();
      }

      finalizeTrackedRequest(originalConfig);

      return Promise.reject(err);
    }
  );
};

export default api;
